import { useCallback, useEffect, useState } from "react";
import { RotateCcw, Play } from "lucide-react";
import { usePrefs } from "@/context/PrefsContext";

const read = (key) => { try { return JSON.parse(localStorage.getItem(key) || "null"); } catch { return null; } };

export function useResumableSession(key, loader) {
  const [data, setData] = useState(null);
  const [saved, setSaved] = useState(() => read(key));
  const [decided, setDecided] = useState(() => !read(key));

  useEffect(() => {
    if (!decided) return;
    if (saved?.data) { setData(saved.data); return; }
    loader().then((d) => { setData(d); localStorage.setItem(key, JSON.stringify({ data: d, answers: {}, idx: 0, at: Date.now() })); });
  }, [decided, key, loader, saved]);

  const onProgress = useCallback((answers, idx) => {
    const cur = read(key) || {};
    if (!cur.data) return;
    localStorage.setItem(key, JSON.stringify({ ...cur, answers, idx, at: Date.now() }));
  }, [key]);

  const resume = () => setDecided(true);
  const startOver = () => { localStorage.removeItem(key); setSaved(null); setDecided(true); };
  const clear = () => localStorage.removeItem(key);
  const answered = saved?.answers ? Object.keys(saved.answers).length : 0;
  return { data, saved: decided ? null : saved, resume, startOver, clear, onProgress, initialAnswers: saved?.answers, initialIdx: saved?.idx || 0, answered };
}

export function ResumePrompt({ saved, answered, onResume, onStartOver, testid = "resume-prompt" }) {
  const { t } = usePrefs();
  if (!saved) return null;
  return (
    <div className="mx-auto max-w-2xl soft-card p-6 fade-up" data-testid={testid}>
      <p className="font-jpserif text-2xl">{t("resumeTitle")}</p>
      <p className="mt-2 text-sm text-muted-foreground">{t("resumeDesc").replace("{n}", (saved.idx || 0) + 1)} · {answered}/{saved.data?.questions?.length || 0}</p>
      <div className="mt-5 flex flex-wrap gap-2">
        <button data-testid="resume-button" onClick={onResume} className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"><Play className="h-4 w-4" />{t("resume")}</button>
        <button data-testid="start-over-button" onClick={onStartOver} className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold hover:bg-accent"><RotateCcw className="h-4 w-4" />{t("startOver")}</button>
      </div>
    </div>
  );
}
